/**
 * @engine/core - Scene Sprite Collector
 *
 * Shared sprite-collection + draw core for the viewport renderers (editor + game/player).
 *
 * Walks a {@link Level}'s `core.SpriteRenderer` components once per frame, resolves textures via an injected
 * resolver, computes world-space size/UV, and produces a list sorted by (sortingLayer, orderInLayer). The
 * submit helpers draw that list through a {@link SpriteBatch} for each render path (GBuffer / Forward+ / unlit).
 *
 * This was duplicated verbatim in EditorViewRenderer and GameViewRenderer; the only real divergences are the
 * texture/material *sources* (editor live-VFS + owned MaterialCache vs player baked + injected resolver) and the
 * HDR-target flag — all injected, so the collection + draw logic lives here once.
 *
 * @module @engine/core
 */

import type {
  IRenderContext,
  Camera2D,
  SpriteBatch,
  TextureHandle,
  BufferHandle,
  LightManager,
  LightCullPass,
  Material,
} from '@engine/render2d';
import type { Level } from '../../Level';

/** A sprite resolved + sized for this frame, in draw order once {@link SceneSpriteCollector.collect} sorts. */
export interface CollectedSprite {
  texture: TextureHandle;
  x: number; y: number; width: number; height: number;
  rotation: number; pivotX: number; pivotY: number;
  u0: number; v0: number; u1: number; v1: number;
  tintR: number; tintG: number; tintB: number; tintA: number;
  /** HDR emissive color (linear, may exceed 1). Consumed by the lit render paths. */
  emR: number; emG: number; emB: number;
  sortingLayer: number;
  orderInLayer: number;
  material?: string;
}

/** Per-frame collaborators the collector needs. Texture/material resolvers are the editor↔player seam. */
export interface SpriteCollectorDeps {
  ctx: IRenderContext;
  camera: Camera2D;
  spriteBatch: SpriteBatch;
  /** Resolve a sprite asset id → loaded GPU texture (or null while loading). Kicks async load on miss. */
  resolveTexture: (spriteId: string) => TextureHandle | null;
  /** Resolve a material guid → built Material (or null while loading / unresolved). */
  resolveMaterial: (guid: string) => Material | null;
}

/** Forward+ / forward-lit draw parameters. */
export interface ForwardLitParams {
  lightManager: LightManager;
  /** When ready, use per-tile Forward+ culling; otherwise fall back to basic forward-lit (all lights). */
  lightCullPass?: LightCullPass | null;
  shadowMapBuffer?: BufferHandle | null;
  iesProfileBuffer?: BufferHandle | null;
  isHdrTarget?: boolean;
}

const PIXELS_PER_UNIT = 100;

export class SceneSpriteCollector {
  /** Sprites collected this frame, sorted by (sortingLayer, orderInLayer). */
  readonly all: CollectedSprite[] = [];
  /** Opaque partition (alpha ≈ 1, no material) — GBuffer-eligible. Filled by {@link partitionForGBuffer}. */
  readonly opaque: CollectedSprite[] = [];
  /** Transparent / material partition — drawn via the forward-lit pass. Filled by {@link partitionForGBuffer}. */
  readonly transparent: CollectedSprite[] = [];

  // Object pool: collect() reuses these across frames instead of allocating a fresh literal per sprite
  // per frame. Every field is overwritten on acquire, so no stale data leaks. Objects are never held
  // across frames by consumers (collect → partition → draw → discard), so reuse is safe.
  private _pool: CollectedSprite[] = [];
  private _poolCount = 0;

  private acquireSprite(): CollectedSprite {
    let s = this._pool[this._poolCount];
    if (s === undefined) {
      s = {
        texture: 0, x: 0, y: 0, width: 0, height: 0,
        rotation: 0, pivotX: 0.5, pivotY: 0.5,
        u0: 0, v0: 0, u1: 1, v1: 1,
        tintR: 1, tintG: 1, tintB: 1, tintA: 1,
        emR: 0, emG: 0, emB: 0,
        sortingLayer: 0, orderInLayer: 0, material: undefined,
      };
      this._pool[this._poolCount] = s;
    }
    this._poolCount++;
    return s;
  }

  /**
   * Walk the level, resolve + size every SpriteRenderer, and build the sorted `all` list. Returns the count.
   * Texture resolution (and its async loading) is delegated to `deps.resolveTexture`.
   */
  collect(level: Level, deps: SpriteCollectorDeps): number {
    const { ctx, resolveTexture } = deps;
    this.all.length = 0;
    this.opaque.length = 0;
    this.transparent.length = 0;
    this._poolCount = 0;

    for (const go of level.getAllObjects()) {
      const spriteData = go.getComponentData('core.SpriteRenderer');
      if (!spriteData) continue;

      const spriteId = spriteData.sprite;
      if (!spriteId) continue;

      const textureHandle = resolveTexture(spriteId);
      if (!textureHandle || textureHandle <= 0) continue;

      const t = go.transform;

      const pivotX = spriteData.pivotX ?? 0.5;
      const pivotY = spriteData.pivotY ?? 0.5;
      const flipX = spriteData.flipX === true;
      const flipY = spriteData.flipY === true;
      const sortingLayer = spriteData.sortingLayer ?? 0;
      const orderInLayer = spriteData.orderInLayer ?? 0;
      const u0 = spriteData.regionU0 ?? 0;
      const v0 = spriteData.regionV0 ?? 0;
      const u1 = spriteData.regionU1 ?? 1;
      const v1 = spriteData.regionV1 ?? 1;
      const sizeW = spriteData.width ?? 0;
      const sizeH = spriteData.height ?? 0;

      const c = spriteData.color;
      const tintR = (c.r ?? 255) / 255;
      const tintG = (c.g ?? 255) / 255;
      const tintB = (c.b ?? 255) / 255;
      const tintA = (c.a ?? 255) / 255;

      // HDR emission (same 0-255 domain as color, unclamped: >255 = HDR)
      const e = spriteData.emission;
      const emR = e ? (e.r ?? 0) / 255 : 0;
      const emG = e ? (e.g ?? 0) / 255 : 0;
      const emB = e ? (e.b ?? 0) / 255 : 0;

      // Determine size (use texture size if 0, accounting for any UV sub-region).
      let w = sizeW, h = sizeH;
      if (w === 0 || h === 0) {
        const texSize = ctx.getTextureSize(textureHandle);
        const tw = texSize?.width || 64;
        const th = texSize?.height || 64;
        const uvW = Math.abs(u1 - u0);
        const uvH = Math.abs(v1 - v0);
        w = (tw * (uvW > 0 ? uvW : 1)) / PIXELS_PER_UNIT;
        h = (th * (uvH > 0 ? uvH : 1)) / PIXELS_PER_UNIT;
      }

      // Apply world scale.
      w *= t.lossyScale.x;
      h *= t.lossyScale.y;

      // UV flip — always flip V (compensates for texture upload convention).
      let fv0 = v1, fv1 = v0, fu0 = u0, fu1 = u1;
      if (flipX) { const tmp = fu0; fu0 = fu1; fu1 = tmp; }
      if (flipY) { const tmp = fv0; fv0 = fv1; fv1 = tmp; }

      const s = this.acquireSprite();
      s.texture = textureHandle;
      s.x = t.position.x; s.y = t.position.y;
      s.width = w; s.height = h;
      s.rotation = t.rotationRadians;
      s.pivotX = pivotX; s.pivotY = pivotY;
      s.u0 = fu0; s.v0 = fv0; s.u1 = fu1; s.v1 = fv1;
      s.tintR = tintR; s.tintG = tintG; s.tintB = tintB; s.tintA = tintA;
      s.emR = emR; s.emG = emG; s.emB = emB;
      s.sortingLayer = sortingLayer;
      s.orderInLayer = orderInLayer;
      s.material = typeof spriteData.material === 'string' && spriteData.material ? spriteData.material : undefined;
      this.all.push(s);
    }

    // Sort by sorting layer, then order within layer. SpriteBatch preserves submission order on flush,
    // so a single sorted pass draws every layer correctly — no per-layer batching needed.
    this.all.sort((a, b) => {
      if (a.sortingLayer !== b.sortingLayer) return a.sortingLayer - b.sortingLayer;
      return a.orderInLayer - b.orderInLayer;
    });

    return this.all.length;
  }

  /**
   * Partition `all` into `opaque` / `transparent` for the deferred (GBuffer) path.
   * Material sprites use a single-target material pipeline → can't write the GBuffer MRT, so they always go to
   * the (forward, swap-chain) material-aware transparent pass.
   */
  partitionForGBuffer(): void {
    this.opaque.length = 0;
    this.transparent.length = 0;
    for (const s of this.all) {
      if (s.material) this.transparent.push(s);
      else if (s.tintA >= 0.999) this.opaque.push(s);
      else this.transparent.push(s);
    }
  }

  /** Draw the opaque partition into the GBuffer (plain textured — the MRT pipeline takes no material). */
  drawOpaqueToGBuffer(deps: SpriteCollectorDeps): void {
    const { spriteBatch, camera } = deps;
    spriteBatch.beginGBuffer(camera);
    for (const s of this.opaque) {
      const drawX = s.x - s.pivotX * s.width;
      const drawY = s.y - s.pivotY * s.height;
      spriteBatch.drawRotated(
        s.texture, drawX, drawY, s.width, s.height,
        -s.rotation, s.pivotX, s.pivotY,
        s.u0, s.v0, s.u1, s.v1,
        s.tintR, s.tintG, s.tintB, s.tintA,
        s.emR, s.emG, s.emB, 1,
      );
    }
    spriteBatch.end();
  }

  /** Draw a list with Forward+ (per-tile culled) lighting, or basic forward-lit when the cull pass isn't ready. */
  drawForwardLit(list: readonly CollectedSprite[], deps: SpriteCollectorDeps, p: ForwardLitParams): void {
    const { spriteBatch, camera } = deps;
    if (p.lightCullPass?.isReady) {
      spriteBatch.beginForwardPlus(camera, p.lightManager, p.lightCullPass, p.isHdrTarget ?? false, p.shadowMapBuffer ?? undefined, p.iesProfileBuffer ?? undefined);
    } else {
      spriteBatch.beginForwardLit(camera, p.lightManager, p.isHdrTarget ?? false);
    }
    for (const s of list) this.drawEntry(s, deps);
    spriteBatch.end();
  }

  /** Draw a list unlit in one batch. */
  drawUnlit(list: readonly CollectedSprite[], deps: SpriteCollectorDeps, isHdrTarget = false): void {
    const { spriteBatch, camera } = deps;
    spriteBatch.begin(camera, isHdrTarget);
    for (const s of list) this.drawEntry(s, deps);
    spriteBatch.end();
  }

  /**
   * Draw one collected sprite — via its material (sprite texture bound as ALBEDO) when it has one and the
   * resolver returns a built Material, else the plain textured path. The batch must already be `begin*`-ed.
   */
  drawEntry(s: CollectedSprite, deps: SpriteCollectorDeps): void {
    const { spriteBatch: b, resolveMaterial } = deps;
    const drawX = s.x - s.pivotX * s.width;
    const drawY = s.y - s.pivotY * s.height;
    const mat = s.material ? resolveMaterial(s.material) : null;
    if (mat) {
      // Pass the sprite texture as ALBEDO instead of mutating the shared cached material —
      // the batch binds it per-flush so sprites sharing one .mat keep their own textures.
      b.drawRotatedWithMaterial(
        mat, drawX, drawY, s.width, s.height,
        -s.rotation, s.pivotX, s.pivotY,
        s.u0, s.v0, s.u1, s.v1,
        s.tintR, s.tintG, s.tintB, s.tintA,
        s.texture,
        s.emR, s.emG, s.emB, 1,
      );
    } else {
      b.drawRotated(
        s.texture, drawX, drawY, s.width, s.height,
        -s.rotation, s.pivotX, s.pivotY,
        s.u0, s.v0, s.u1, s.v1,
        s.tintR, s.tintG, s.tintB, s.tintA,
        s.emR, s.emG, s.emB, 1,
      );
    }
  }
}
